import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Spin, Tabs, Button } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import RoundedModal from '@components/roundedModal/roundedModal';
import CreateEnvironmentModal from '@components/CreateEnvironmentModal/CreateEnvironmentModal';
import useTranslation from '@hooks/useTranslation';
import useKemuNotification from '@common/hooks/useKemuNotification';
import { selectManageEnvironmentsModalVisible, showManageEnvironmentsModal } from '@src/features/interface/interfaceSlice';
import deploymentApi, { Environment, WorkerReport } from '@src/api/deployment/deploymentApi';
import { ReactComponent as ManageEnvironmentsIcon } from '@src/assets/img/menus/manage-environments.svg';
import KemuSwitch from '@components/form-control/kemuSwitch/KemuSwitch';
import styles from './ManageEnvironmentsModal.module.css';
import { STOP_SHORTCUT_PROPAGATION_CLASS } from '@common/constants';
import OverviewTab from './OverviewTab';
import EnvironmentsTab from './EnvironmentsTab';
import DeploymentsTab from './DeploymentsTab';
import ProcessesTab from './ProcessesTab';
import { LoadingOutlined } from '@ant-design/icons';

/**
 * Modal component for managing environments, hosts, and deployments
 */
const ManageEnvironmentsModal: React.FC = () => {
	const dispatch = useDispatch();
	const visible = useSelector(selectManageEnvironmentsModalVisible);
	const t = useTranslation('ManageEnvironmentsModal');
	const tCommon = t.withBaseKey('CommonWords');
	const showNotification = useKemuNotification();

	const [editingEnvironment, setEditingEnvironment] = useState<Environment | null>(null);
	const [showEditModal, setShowEditModal] = useState(false);
	const [viewingLogsWorker, setViewingLogsWorker] = useState<WorkerReport | null>(null);
	const [viewingLogsWorkerData, setViewingLogsWorkerData] = useState<WorkerReport | null>(null);
	const [loadingLogs, setLoadingLogs] = useState(false);
	const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
	const autoRefreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const [activeTab, setActiveTab] = useState<string>('overview');
	const [showCreateEnvironmentModal, setShowCreateEnvironmentModal] = useState(false);
	const [environmentsRefreshKey, setEnvironmentsRefreshKey] = useState(0);


	/**
	 * Handles editing an environment
	 */
	const handleEditEnvironment = useCallback((environment: Environment) => {
		setEditingEnvironment(environment);
		setShowEditModal(true);
	}, []);

	/**
	 * Handles environment update after edit
	 */
	const handleEnvironmentUpdated = useCallback((updated: Environment) => {
		setShowEditModal(false);
		setEditingEnvironment(null);
		// Trigger refresh in EnvironmentsTab
		setEnvironmentsRefreshKey(prev => prev + 1);
	}, []);

	/**
	 * Handles environment creation
	 */
	const handleEnvironmentCreated = useCallback((created: Environment) => {
		setShowCreateEnvironmentModal(false);
		// Trigger refresh in EnvironmentsTab
		setEnvironmentsRefreshKey(prev => prev + 1);
	}, []);

	/**
	 * Handles deleting an environment
	 */
	const handleDeleteEnvironment = useCallback(async (environmentId: string) => {
		try {
			await deploymentApi.deleteEnvironment(environmentId);
			showNotification({
				type: 'success',
				message: t('Environments.Delete.Success'),
			});
			// Trigger refresh in EnvironmentsTab
			setEnvironmentsRefreshKey(prev => prev + 1);
		} catch (err) {
			console.error('Failed to delete environment:', err);
			showNotification({
				type: 'error',
				title: t('Environments.Delete.Error'),
				message: err?.message || "Unknown error",
			});
		}
	}, [t, showNotification]);

	/**
	 * Handles opening the log modal and fetching fresh worker data
	 */
	const handleViewLogs = useCallback(async (worker: WorkerReport) => {
		setViewingLogsWorker(worker);
		setLoadingLogs(true);
		try {
			const workerData = await deploymentApi.getWorker(worker.workerId);
			setViewingLogsWorkerData(workerData);
		} catch (err) {
			console.error('Failed to load worker logs:', err);
			// Fallback to the worker data we already have
			setViewingLogsWorkerData(worker);
		} finally {
			setLoadingLogs(false);
		}
	}, []);

	/**
	 * Handles refreshing the worker logs by fetching fresh data from the API
	 * @param scheduleNextRefresh whether to schedule the next auto-refresh after this one completes
	 */
	const handleRefreshLogs = useCallback(async (scheduleNextRefresh: boolean = false) => {
		if (!viewingLogsWorker) return;
		
		setLoadingLogs(true);
		try {
			const workerData = await deploymentApi.getWorker(viewingLogsWorker.workerId);
			setViewingLogsWorkerData(workerData);
		} catch (err) {
			console.error('Failed to refresh worker logs:', err);
			// Keep existing data on error
		} finally {
			setLoadingLogs(false);
			
			// Schedule next refresh if auto-refresh is enabled
			if (scheduleNextRefresh && autoRefreshEnabled && viewingLogsWorker) {
				if (autoRefreshTimeoutRef.current) {
					clearTimeout(autoRefreshTimeoutRef.current);
				}
				autoRefreshTimeoutRef.current = setTimeout(() => {
					handleRefreshLogs(true);
				}, 1000);
			}
		}
	}, [viewingLogsWorker, autoRefreshEnabled]);

	/**
	 * Handles toggling auto-refresh on/off
	 */
	const handleAutoRefreshToggle = useCallback((checked: boolean) => {
		setAutoRefreshEnabled(checked);
	}, []);

	/**
	 * Handles closing the log modal
	 */
	const handleCloseLogs = useCallback(() => {
		setViewingLogsWorker(null);
		setViewingLogsWorkerData(null);
		setAutoRefreshEnabled(false);
		// Clear any pending timeout
		if (autoRefreshTimeoutRef.current) {
			clearTimeout(autoRefreshTimeoutRef.current);
			autoRefreshTimeoutRef.current = null;
		}
	}, []);


	// Auto-refresh logs when enabled
	useEffect(() => {
		if (autoRefreshEnabled && viewingLogsWorker && !loadingLogs) {
			// Clear any existing timeout
			if (autoRefreshTimeoutRef.current) {
				clearTimeout(autoRefreshTimeoutRef.current);
			}
			
			// Schedule the first refresh after 1 second
			autoRefreshTimeoutRef.current = setTimeout(() => {
				handleRefreshLogs(true);
			}, 1000);
		} else {
			// Clear timeout when auto-refresh is disabled or modal is closed
			if (autoRefreshTimeoutRef.current) {
				clearTimeout(autoRefreshTimeoutRef.current);
				autoRefreshTimeoutRef.current = null;
			}
		}

		// Cleanup on unmount
		return () => {
			if (autoRefreshTimeoutRef.current) {
				clearTimeout(autoRefreshTimeoutRef.current);
				autoRefreshTimeoutRef.current = null;
			}
		};
	}, [autoRefreshEnabled, viewingLogsWorker, loadingLogs, handleRefreshLogs]);

	const handleClose = useCallback(() => {
		dispatch(showManageEnvironmentsModal(false));
		setActiveTab('overview');
	}, [dispatch]);

	return (
		<>
			{editingEnvironment && (
				<CreateEnvironmentModal
					visible={showEditModal}
					onClose={() => {
						setShowEditModal(false);
						setEditingEnvironment(null);
					}}
					onSuccess={handleEnvironmentUpdated}
					initialData={editingEnvironment}
				/>
			)}
			{showCreateEnvironmentModal && (
				<CreateEnvironmentModal
					visible={showCreateEnvironmentModal}
					onClose={() => setShowCreateEnvironmentModal(false)}
					onSuccess={handleEnvironmentCreated}
				/>
			)}
			<RoundedModal
				visible={visible}
				onCancel={handleClose}
				title={
					<div className={styles.TitleContainer}>
						<ManageEnvironmentsIcon className={styles.TitleIcon} />
						<span>{t('Title')}</span>
					</div>
				}
				width={'80%'}
				centered={true}
				// height={'80%'}
				dialogContainerClassName={`${styles.DialogContainer} ${STOP_SHORTCUT_PROPAGATION_CLASS}`}
				okBtnLabel={tCommon('Close')}
				onOk={handleClose}
				closeOnMaskClick={false}
			>
				<div className={styles.Container}>
					<Tabs
						activeKey={activeTab}
						onChange={setActiveTab}
						items={[
							{
								key: 'overview',
								label: t('Tabs.Overview'),
							},
							{
								key: 'environments',
								label: t('Tabs.Environments'),
							},
							{
								key: 'deployments',
								label: t('Tabs.Deployments'),
							},
							{
								key: 'processes',
								label: t('Tabs.Processes'),
							},
						]}
						className={styles.Tabs}
					/>

					{activeTab === 'overview' && (
						<OverviewTab
							visible={visible && activeTab === 'overview'}
							onNavigateToTab={setActiveTab}
							onCreateEnvironment={() => setShowCreateEnvironmentModal(true)}
						/>
					)}
					{activeTab === 'environments' && (
						<EnvironmentsTab
							visible={visible && activeTab === 'environments'}
							refreshTrigger={environmentsRefreshKey}
							onEditEnvironment={handleEditEnvironment}
							onDeleteEnvironment={handleDeleteEnvironment}
						/>
					)}
					{activeTab === 'deployments' && <DeploymentsTab />}
					{activeTab === 'processes' && <ProcessesTab onViewLogs={handleViewLogs} />}
				</div>
			</RoundedModal>
			{viewingLogsWorker && (
				<RoundedModal
					visible={!!viewingLogsWorker}
					onCancel={handleCloseLogs}
					dialogContainerClassName={styles.LogModalDialogContainer}
					title={t('LogModal.Title')}
					width={800}
					height={'auto'}
					okBtnLabel={tCommon('Close')}
					onOk={handleCloseLogs}
					closeOnMaskClick={false}
					tabIndex={0}
					className={STOP_SHORTCUT_PROPAGATION_CLASS}
				>
					<div className={styles.LogModalContent}>
						<div className={styles.LogModalHeader}>
							<div className={styles.LogModalInfo}>
								{viewingLogsWorkerData ? (
									<>
										<span>{t('LogModal.WorkerId', { workerId: viewingLogsWorkerData.workerId })}</span>
										{viewingLogsWorkerData.pid && (
											<span>{t('LogModal.Pid', { pid: viewingLogsWorkerData.pid })}</span>
										)}
									</>
								) : (
									<>
										<span>{t('LogModal.WorkerId', { workerId: viewingLogsWorker.workerId })}</span>
										{viewingLogsWorker.pid && (
											<span>{t('LogModal.Pid', { pid: viewingLogsWorker.pid })}</span>
										)}
									</>
								)}
							</div>
							{!autoRefreshEnabled && (
								<Button
									onClick={() => handleRefreshLogs(false)}
									disabled={loadingLogs}
									className={styles.RefreshButton}
								>
									{t('LogModal.Refresh')}
								</Button>
							)}
						</div>
						{/* The tabIndex is used to allow the logs to receive keyboard events */}
						<Spin spinning={loadingLogs && !autoRefreshEnabled} indicator={<LoadingOutlined />}>
							<div className={styles.AutoRefreshToggle}>
								<span className={styles.AutoRefreshLabel}>{t('LogModal.AutoRefresh')}</span>
								<KemuSwitch
									size="small"
									checked={autoRefreshEnabled}
									onChange={handleAutoRefreshToggle}
								/>
							</div>
							<div className={styles.LogContent} tabIndex={0}>
								<div className={styles.LogContentContainer}>
									{loadingLogs && !autoRefreshEnabled ? (
										<div className={styles.NoLogs}>
											<br/><br/><br/>
											{tCommon('Loading')}...
											</div>
									) : (() => {
										const logs = viewingLogsWorkerData?.lastLogLines || viewingLogsWorker.lastLogLines;
										return logs && logs.length > 0 ? (
											<pre className={styles.LogText}>
												{logs.join('\n')}
											</pre>
										) : (
											<div className={styles.NoLogs}>{t('LogModal.NoLogs')}</div>
										);
									})()}
								</div>
							</div>
						</Spin>
					</div>
				</RoundedModal>
			)}
		</>
	);
};

export default ManageEnvironmentsModal;

